import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, BadgeCheck, Leaf, MapPin, Heart } from "lucide-react";
import { MaskedLines, Reveal, RevealLine } from "@/components/Motion";
import { IMAGES, TRUST } from "@/data/site";

const iconMap = { ShieldCheck, BadgeCheck, Leaf, MapPin };
const STATS = [
  { n: "100+", l: "Jobs completed" },
  { n: "100%", l: "Bond-back focus" },
  { n: "4.5", l: "Average rating" },
  { n: "12+", l: "Suburbs served" },
];

export default function About() {
  return (
    <div>
      <section className="pt-40 pb-16 bg-[#FAFAF9]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <p className="kicker mb-6">About Britelines</p>
            <h1 className="font-display font-extrabold tracking-tight text-[#1C2A39] text-5xl sm:text-7xl leading-[0.95]">
              <MaskedLines lines={["Adelaide's own", "cleaning team."]} />
            </h1>
          </div>
          <Reveal delay={0.4} className="lg:col-span-5">
            <p className="text-lg text-[#475569] leading-relaxed">
              Born and based in Adelaide, Britelines was built on a simple idea: treat every home and office like our own. Today we're one of Adelaide's most trusted local cleaning teams.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 bg-[#FAFAF9]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <div className="img-hover-wrap clip-arch h-[50vh] sm:h-[65vh]">
            <img src={IMAGES.cleaner} alt="Professional Britelines cleaner" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 bg-white border-y border-slate-100">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <Reveal key={s.l} delay={i * 0.08}>
              <div>
                <p className="font-display font-extrabold text-5xl sm:text-6xl text-[#1C2A39] tracking-tight">{s.n}</p>
                <p className="text-[#475569] mt-2">{s.l}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* STORY + TRUST */}
      <section className="py-24 sm:py-32 bg-[#FAFAF9]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="kicker mb-4">Why locals trust us</p>
            <h2 className="font-display font-medium text-4xl sm:text-5xl tracking-tight text-[#1C2A39] leading-[1.05]">
              <RevealLine>Proudly local.</RevealLine>
              <RevealLine delay={0.1}>Genuinely trusted.</RevealLine>
            </h2>
            <p className="mt-6 text-[#475569] leading-relaxed">
              Every member of our team is police-checked and reliable, so you can hand over your keys with total peace of mind. We use eco-friendly products, show up on time, and don't consider a job done until it's truly spotless — backed by our 100% satisfaction guarantee.
            </p>
            <Link to="/quote" className="btn-primary mt-8">Work with us <ArrowRight size={18} /></Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {TRUST.map((t, i) => {
              const Icon = iconMap[t.icon] || Heart;
              return (
                <Reveal key={t.label} delay={i * 0.08}>
                  <div className="bg-white rounded-3xl p-7 border border-slate-200 h-full">
                    <div className="w-12 h-12 rounded-2xl bg-[#E8F1EB] flex items-center justify-center mb-5">
                      <Icon size={22} className="text-[#0D9488]" strokeWidth={1.7} />
                    </div>
                    <h3 className="font-display font-medium text-xl text-[#1C2A39]">{t.label}</h3>
                    <p className="text-sm text-[#475569] mt-2">{t.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
